import { Button, Chip, ConnectionBar, faDigits, formatMoney } from "@kitchen/ui";
import { useEffect, useState } from "react";
import {
  createPublicFoodpartyReservation,
  getPublicFoodpartyListings,
  getPublicMyReservations,
  requestPublicFoodpartyOtp,
  verifyPublicFoodpartyOtp,
} from "../../api.js";
import { useHeartbeat } from "../../heartbeat.js";
import type { PublicListingView, PublicReservationView } from "@kitchen/schemas";

const TOKEN_KEY = "foodparty_public_token";
const PHONE_KEY = "foodparty_public_phone";

export function PublicFoodpartyPage() {
  const hb = useHeartbeat();
  const [tab, setTab] = useState<"listings" | "my-reservations">("listings");
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
  const [phone, setPhone] = useState<string | null>(() => localStorage.getItem(PHONE_KEY));

  const [listings, setListings] = useState<PublicListingView[]>([]);
  const [myReservations, setMyReservations] = useState<PublicReservationView[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Auth modal state
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [inputPhone, setInputPhone] = useState("");
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [otpCode, setOtpCode] = useState("");
  const [authStep, setAuthStep] = useState<"phone" | "otp">("phone");
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  // Reservation dialog state
  const [selectedListing, setSelectedListing] = useState<PublicListingView | null>(null);
  const [reservePortions, setReservePortions] = useState(1);
  const [reserving, setReserving] = useState(false);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      if (tab === "listings") {
        const data = await getPublicFoodpartyListings();
        setListings(data);
      } else {
        if (token) {
          const data = await getPublicMyReservations(token);
          setMyReservations(data);
        }
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "خطا در بارگذاری اطلاعات";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, [tab, token, phone]);

  const handleRequestOtp = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    const cleanPhone = inputPhone.trim();
    if (!cleanPhone) {
      setAuthError("لطفاً شماره همراه خود را وارد کنید.");
      return;
    }
    setAuthLoading(true);
    setAuthError(null);
    try {
      // Normalise to +98 format if starts with 09
      const normalised = cleanPhone.startsWith("0") ? `+98${cleanPhone.slice(1)}` : cleanPhone;
      const res = await requestPublicFoodpartyOtp(normalised);
      setChallengeId(res.challengeId);
      setAuthStep("otp");
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : "خطا در ارسال کد تایید؛ لطفاً کمی بعد دوباره تلاش کنید";
      setAuthError(msg);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    if (!challengeId || !otpCode.trim()) {
      setAuthError("لطفاً کد تایید ۶ رقمی را وارد کنید.");
      return;
    }
    setAuthLoading(true);
    setAuthError(null);
    try {
      const cleanPhone = inputPhone.trim();
      const normalised = cleanPhone.startsWith("0") ? `+98${cleanPhone.slice(1)}` : cleanPhone;
      const res = await verifyPublicFoodpartyOtp(challengeId, otpCode.trim(), normalised);
      setToken(res.token);
      setPhone(res.phone);
      localStorage.setItem(TOKEN_KEY, res.token);
      localStorage.setItem(PHONE_KEY, res.phone);
      setShowAuthModal(false);
      setAuthStep("phone");
      setOtpCode("");
      setInputPhone("");
      setSuccessMsg("با موفقیت وارد شدید.");
      await loadData();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "کد تایید نامعتبر یا منقضی شده است";
      setAuthError(msg);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(PHONE_KEY);
    setToken(null);
    setPhone(null);
    setMyReservations([]);
  };

  const handleStartReserve = (listing: PublicListingView) => {
    if (!token) {
      setSelectedListing(listing);
      setShowAuthModal(true);
      return;
    }
    setSelectedListing(listing);
    setReservePortions(1);
  };

  const handleConfirmReserve = async () => {
    if (!selectedListing) return;
    setReserving(true);
    setError(null);
    try {
      const activeToken = token ?? undefined;
      const res = await createPublicFoodpartyReservation(
        {
          listingId: selectedListing.id,
          portions: reservePortions,
          paymentMethod: "counter",
        },
        activeToken,
      );
      setSelectedListing(null);
      setSuccessMsg(`رزرو شما با موفقیت ثبت شد! کد تحویل: ${res.deliveryCode}`);
      setTab("my-reservations");
      await loadData();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "خطا در ثبت رزرو";
      setError(msg);
    } finally {
      setReserving(false);
    }
  };

  return (
    <div
      className="public-foodparty-container"
      dir="rtl"
      style={{ maxWidth: 640, margin: "0 auto", padding: "16px" }}
    >
      {/* Header */}
      <header style={{ marginBottom: 24, textAlign: "center" }}>
        <div
          style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}
        >
          <ConnectionBar state={hb.state} lastOkAt={hb.lastOkAt} now={hb.now} />
          {token ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 13, color: "var(--ink-subtle, #666)" }}>
                {phone ? faDigits(phone) : ""}
              </span>
              <Button variant="ghost" onClick={handleLogout}>
                خروج
              </Button>
            </div>
          ) : (
            <Button
              variant="default"
              onClick={() => {
                setShowAuthModal(true);
              }}
            >
              ورود / ثبت‌نام
            </Button>
          )}
        </div>

        <h1 style={{ fontSize: 24, fontWeight: 700, margin: "0 0 6px 0", color: "var(--ink-strong, #111)" }}>
          🔥 فودپارتی آشپزخانه مرکزی
        </h1>
        <p style={{ fontSize: 14, color: "var(--ink-subtle, #666)", margin: 0 }}>
          غذای تازه و باکیفیت با تخفیف ویژه برای جلوگیری از اسراف
        </p>
      </header>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        <button
          type="button"
          className={`btn ${tab === "listings" ? "btn-primary" : "btn-ghost"}`}
          style={{ flex: 1, height: 44 }}
          onClick={() => {
            setTab("listings");
          }}
        >
          غذاهای آماده رزرو ({faDigits(listings.length)})
        </button>
        <button
          type="button"
          className={`btn ${tab === "my-reservations" ? "btn-primary" : "btn-ghost"}`}
          style={{ flex: 1, height: 44 }}
          onClick={() => {
            setTab("my-reservations");
          }}
        >
          رزروهای من {myReservations.length > 0 && `(${faDigits(myReservations.length)})`}
        </button>
      </div>

      {/* Notifications */}
      {error && (
        <div
          style={{
            padding: 12,
            backgroundColor: "#fee2e2",
            color: "#991b1b",
            borderRadius: 12,
            marginBottom: 16,
          }}
        >
          {error}
        </div>
      )}
      {successMsg && (
        <div
          style={{
            padding: 12,
            backgroundColor: "#dcfce7",
            color: "#166534",
            borderRadius: 12,
            marginBottom: 16,
          }}
        >
          {successMsg}
        </div>
      )}

      {/* Content: Listings */}
      {tab === "listings" && (
        <div>
          {loading && <p style={{ textAlign: "center", color: "#888" }}>در حال بارگذاری غذاها...</p>}

          {!loading && listings.length === 0 && (
            <div className="card" style={{ textAlign: "center", padding: 32 }}>
              <p style={{ fontSize: 16, color: "#666", margin: "0 0 8px 0" }}>
                در حال حاضر همه غذاهای فودپارتی رزرو شده‌اند.
              </p>
              <p style={{ fontSize: 13, color: "#999", margin: 0 }}>
                پس از پایان پخت یا کنسلی جدید، غذاهای مازاد بلافاصله اینجا قرار می‌گیرند.
              </p>
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {listings.map((l) => (
              <div key={l.id} className="card" style={{ padding: 16 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: 10,
                  }}
                >
                  <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>{l.foodName}</h3>
                  <Chip tone="coral">{faDigits(l.discountPercent)}٪ تخفیف</Chip>
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 12,
                  }}
                >
                  <div>
                    <span style={{ fontSize: 18, fontWeight: 700, color: "var(--primary, #059669)" }}>
                      {formatMoney(l.finalPriceRial, "toman")}
                    </span>
                    <span style={{ fontSize: 12, color: "#888", marginRight: 6 }}>هر پرس</span>
                  </div>
                  <Chip tone="blue">تنها {faDigits(l.portionsAvailable)} پرس باقی مانده</Chip>
                </div>

                <div style={{ fontSize: 12, color: "#666", marginBottom: 14, lineHeight: 1.6 }}>
                  <div>📍 محل دریافت: {l.pickupPlace}</div>
                  <div>⏰ مهلت دریافت: تا ساعت {faDigits(l.pickupDeadline.slice(11, 16))}</div>
                </div>

                <Button
                  variant="primary"
                  block
                  disabled={l.portionsAvailable <= 0}
                  onClick={() => {
                    handleStartReserve(l);
                  }}
                >
                  رزرو و دریافت کد تحویل
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Content: My Reservations */}
      {tab === "my-reservations" && (
        <div>
          {!token ? (
            <div className="card" style={{ textAlign: "center", padding: 32 }}>
              <p style={{ fontSize: 15, color: "#444", marginBottom: 16 }}>
                برای مشاهده رزروها و کدهای تحویل خود، لطفاً با شماره همراه وارد شوید.
              </p>
              <Button
                variant="primary"
                onClick={() => {
                  setShowAuthModal(true);
                }}
              >
                ورود با شماره همراه
              </Button>
            </div>
          ) : (
            <div>
              {loading && <p style={{ textAlign: "center", color: "#888" }}>در حال دریافت رزروها...</p>}

              {!loading && myReservations.length === 0 && (
                <div className="card" style={{ textAlign: "center", padding: 32 }}>
                  <p style={{ fontSize: 15, color: "#666", margin: 0 }}>شما در حال حاضر رزرو فعالی ندارید.</p>
                </div>
              )}

              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {myReservations.map((r) => (
                  <div key={r.id} className="card" style={{ padding: 16 }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        marginBottom: 8,
                      }}
                    >
                      <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
                        {r.foodName} ({faDigits(r.portions)} پرس)
                      </h3>
                      <Chip tone={r.status === "delivered" ? "ok" : "warn"}>
                        {r.status === "delivered" ? "تحویل داده شد" : "در انتظار تحویل"}
                      </Chip>
                    </div>

                    {/* Delivery Code Display */}
                    <div
                      style={{
                        backgroundColor: "#f1f5f9",
                        border: "2px dashed #94a3b8",
                        borderRadius: 12,
                        padding: "12px 16px",
                        margin: "12px 0",
                        textAlign: "center",
                      }}
                    >
                      <div style={{ fontSize: 12, color: "#64748b", marginBottom: 4 }}>
                        کد تحویل به پیشخوان
                      </div>
                      <div
                        id="delivery-code-display"
                        data-code={r.deliveryCode}
                        style={{
                          fontSize: 28,
                          fontWeight: 800,
                          letterSpacing: "4px",
                          color: "#0f172a",
                          fontFamily: "monospace",
                        }}
                      >
                        {faDigits(r.deliveryCode)}
                      </div>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: 13,
                        color: "#555",
                      }}
                    >
                      <span>مبلغ قابل پرداخت: {formatMoney(r.totalPriceRial, "toman")}</span>
                      <span>محل تحویل: {r.pickupPlace}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Reservation Quantity Modal */}
      {selectedListing && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
            zIndex: 100,
          }}
        >
          <div
            style={{ backgroundColor: "#fff", borderRadius: 16, padding: 24, width: "100%", maxWidth: 400 }}
          >
            <h3 style={{ margin: "0 0 12px 0", fontSize: 18 }}>رزرو {selectedListing.foodName}</h3>
            <p style={{ fontSize: 14, color: "#666", marginBottom: 16 }}>
              قیمت هر پرس: {formatMoney(selectedListing.finalPriceRial, "toman")} (پرداخت در پیشخوان)
            </p>

            <div style={{ marginBottom: 20 }}>
              <label style={{ display: "block", fontSize: 13, marginBottom: 6 }}>تعداد پرس:</label>
              <select
                id="fp-portions-select"
                value={reservePortions}
                onChange={(e) => {
                  setReservePortions(Number(e.target.value));
                }}
                style={{
                  width: "100%",
                  height: 44,
                  padding: "0 12px",
                  borderRadius: 8,
                  border: "1px solid #ccc",
                }}
              >
                {Array.from({ length: Math.min(selectedListing.portionsAvailable, 5) }, (_, i) => i + 1).map(
                  (n) => (
                    <option key={n} value={n}>
                      {faDigits(n)} پرس ({formatMoney(Number(selectedListing.finalPriceRial) * n, "toman")})
                    </option>
                  ),
                )}
              </select>
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <Button
                variant="primary"
                block
                disabled={reserving}
                onClick={() => {
                  void handleConfirmReserve();
                }}
              >
                {reserving ? "در حال ثبت..." : "تأیید و دریافت کد"}
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setSelectedListing(null);
                }}
              >
                انصراف
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Auth Modal */}
      {showAuthModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
            zIndex: 110,
          }}
        >
          <div
            style={{ backgroundColor: "#fff", borderRadius: 16, padding: 24, width: "100%", maxWidth: 400 }}
          >
            <h3 style={{ margin: "0 0 8px 0", fontSize: 18 }}>ورود به فودپارتی</h3>
            <p style={{ fontSize: 13, color: "#666", marginBottom: 16 }}>
              {authStep === "phone"
                ? "شماره همراه خود را وارد کنید تا کد تأیید برای شما ارسال شود."
                : "کد تأیید ۶ رقمی پیامک‌شده را وارد کنید."}
            </p>

            {authError && (
              <div
                style={{
                  padding: 10,
                  backgroundColor: "#fee2e2",
                  color: "#991b1b",
                  borderRadius: 8,
                  fontSize: 13,
                  marginBottom: 12,
                }}
              >
                {authError}
              </div>
            )}

            {authStep === "phone" ? (
              <form
                onSubmit={(e) => {
                  void handleRequestOtp(e);
                }}
              >
                <div style={{ marginBottom: 16 }}>
                  <label htmlFor="fp-phone" style={{ display: "block", fontSize: 13, marginBottom: 6 }}>
                    شماره همراه:
                  </label>
                  <input
                    id="fp-phone"
                    type="tel"
                    placeholder="۰۹۱۲۳۴۵۶۷۸۹"
                    value={inputPhone}
                    onChange={(e) => {
                      setInputPhone(e.target.value);
                    }}
                    dir="ltr"
                    style={{
                      width: "100%",
                      height: 44,
                      padding: "0 12px",
                      borderRadius: 8,
                      border: "1px solid #ccc",
                      fontSize: 16,
                    }}
                  />
                </div>
                <div style={{ display: "flex", gap: 10 }}>
                  <Button id="fp-btn-send-otp" type="submit" variant="primary" block disabled={authLoading}>
                    {authLoading ? "در حال ارسال..." : "ارسال کد تأیید"}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setShowAuthModal(false);
                    }}
                  >
                    بستن
                  </Button>
                </div>
              </form>
            ) : (
              <form
                onSubmit={(e) => {
                  void handleVerifyOtp(e);
                }}
              >
                <div style={{ marginBottom: 16 }}>
                  <label htmlFor="fp-otp" style={{ display: "block", fontSize: 13, marginBottom: 6 }}>
                    کد تأیید:
                  </label>
                  <input
                    id="fp-otp"
                    type="text"
                    placeholder="۱۲۳۴۵۶"
                    value={otpCode}
                    onChange={(e) => {
                      setOtpCode(e.target.value);
                    }}
                    dir="ltr"
                    maxLength={6}
                    style={{
                      width: "100%",
                      height: 48,
                      textAlign: "center",
                      letterSpacing: "8px",
                      fontSize: 22,
                      fontFamily: "monospace",
                      borderRadius: 8,
                      border: "1px solid #ccc",
                    }}
                  />
                </div>
                <div style={{ display: "flex", gap: 10 }}>
                  <Button id="fp-btn-verify-otp" type="submit" variant="primary" block disabled={authLoading}>
                    {authLoading ? "در حال بررسی..." : "تأیید و ورود"}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setAuthStep("phone");
                    }}
                  >
                    بازگشت
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
